import _ from 'lodash';
import BaseCtrl from 'edge/platform/base/BaseCtrl';
import {PhoneNumberUtil} from 'libphonenumber';
import ApplicationFeatures from 'gw-portals-featuredetection-js/ApplicationFeatures';
import nameUtil from 'edge/gateway/util/NameUtil';

const fn = ($translate, $scope, $state, $q, $stateParams, AccountService, ActivityService, UserService, UserInfoService, TranslateService, ModalService, InsuranceSuiteMetadata, defaultLocaleDetails) => {
    const ctrl = Object.create(BaseCtrl);
    ctrl.init = () => {
        initialiseScopeVariables();
        $scope.getAccountDetails();
        getAccountBillingData($stateParams.accountNumber);
        addRecentlyViewedAccounts();
        ctrl.shouldShowAddActivityButton();
        shouldShowJourneyButton($stateParams.accountNumber);
        shouldShowCommissionsButton();
        $scope.showComplaintActivity = true;
    };

    /* Private Functions*/
    function initialiseScopeVariables() {
        $scope.accountInfo = {
            loaded: false
        };
        $scope.accountNumber = $stateParams.accountNumber;
        $scope.isDelinquent = false;
        $scope.billingAndPaymentIcon = 'fa-thumbs-o-up fa-check';

        $scope.policyTransactionTableConfig = {
            transactionStatus: '',
            policyTransactionStatus: [],
            source: 'account',
            search: ''
        };

        getPolicyTransactionStatus();

        /* Activity Summary Config - Passed into Activity Summary Directive*/
        $scope.activityTableConfig = {
            actStatus: 'allOpen',
            defaultActStatus: 'allOpen',
            actStatusOptions: {
                'allOpen': 'gateway.directives.ActivitySummary.All Open',
                'allClosed': 'gateway.directives.ActivitySummary.All Closed',
                'overdue': 'gateway.directives.ActivitySummary.Overdue',
                'openAssignedToMe': 'gateway.directives.ActivitySummary.Open, Assigned to me'
            },
            showAddActivity: false
        };

        /* Quote Table config* - Passed into Job Summary Directive*/
        $scope.quoteTableConfig = {
            jobStatus: 'openNotBound',
            defaultJobStatus: 'openNotBound',
            issuedInLastXDays: 30,
            jobStatusOptions: {
                'last30Days': 'gateway.views.quote-detail.last30Days',
                'openNotBound': 'gateway.views.quote-detail.openNotBound',
                'openBound': 'gateway.views.quote-detail.openBound'
            },
            source: 'account',
            search: ''
        };

        const applicationFeatures = ApplicationFeatures.getAllFeatures();
        $scope.enableClaims = applicationFeatures.gatewayclaims === 'true';
        $scope.billingFeatureTurnedOn = ApplicationFeatures.getAllFeatures().gatewayBilling === 'true';
        $scope.activitiesTurnedOnPSA = ApplicationFeatures.getAllFeatures().disableActivities_PSA !== 'true'; // PSA
        $scope.notesTurnedOnPSA = ApplicationFeatures.getAllFeatures().disableNotes_PSA !== 'true'; // PSA
        $scope.documentsTurnedOnPSA = ApplicationFeatures.getAllFeatures().disableDocuments_PSA !== 'true'; // PSA
        $scope.userDefaultCountry = UserInfoService.user.userDefaultCountry_PSA; // PSA
    }

    $scope.getAccountDetails = () => {
        const accountDetailsPromise = AccountService.getAccountDetails($scope.accountNumber);
        $scope.accountDetailsPromise = accountDetailsPromise;

        return $q.when(accountDetailsPromise)
            .then($scope.mapAccountDetails)
            .then(() => {
                $scope.accountInfo.loaded = true;
            })
            .catch(() => {
                const msg = $translate('gateway.views.account-detail.findAccount.noaccount', {
                    accountNumber: $scope.accountNumber
                });
                ModalService.showError('gateway.views.modal.Error', msg);
                $state.go('accounts.index');
            });
    };

    function shouldShowJourneyButton(accountNumber) {
        $scope.journeyTurnedOn = false;
        if (ApplicationFeatures.hasFeature('journey')) {
            const policyViewPermCheckPromise = UserService.hasUserAccessToAccountPolicies(accountNumber);
            policyViewPermCheckPromise.then((allowed) => {
                $scope.journeyTurnedOn = allowed;
            }).catch(()=>{
                $scope.journeyTurnedOn = false;
            });
        }
    }

    function shouldShowCommissionsButton() {
        const permissionDTO = {
            permission: 'viewcommissions'
        };

        UserService.hasUserSystemPermission(permissionDTO).then((canViewCommissionStatements) => {
            $scope.showCommissions = canViewCommissionStatements;
        });
    }

    $scope.mapAccountDetails = (accountDetails) => {
        if (!accountDetails) {
            return;
        }

        $scope.accountInfo.publicID = accountDetails.publicID;
        $scope.accountInfo.policySummaries = _.filter(accountDetails.policySummaries, {
            isIssued: true
        });
        $scope.accountInfo.accountNumber = accountDetails.accountNumber;
        $scope.accountInfo.canCreateSub = accountDetails.canUserCreateSubmission;
        $scope.accountInfo.accountBillingSummary = accountDetails.accountBillingSummary;
        $scope.accountInfo.accountCreatedDate = accountDetails.accountCreatedDate;
        $scope.accountInfo.statusDisplayName = accountDetails.statusDisplayName;
        $scope.accountInfo.serviceTier = accountDetails.serviceTier;
        $scope.accountInfo.accountBillingSummary = accountDetails.accountBillingSummary;
        $scope.accountInfo.producerCodes = accountDetails.producerCodes;
        $scope.accountInfo.totalPremium = accountDetails.totalPremium;

        $scope.accountSummary = {
            numberOfOpenActivities: accountDetails.numberOfOpenActivities,
            numberOfNotes: accountDetails.numberOfNotes,
            numberOfContacts: accountDetails.numberOfContacts,
            numberOfDocuments: accountDetails.numberOfDocuments,
            numberOfOpenClaims: accountDetails.numberOfOpenClaims,
            numberOfWorkOrders: accountDetails.numberOfWorkOrders,
            numberOfOpenQuotes: accountDetails.numberOfOpenQuotes,
            numberOfOpenPolicyRenewals: accountDetails.numberOfOpenPolicyRenewals,
            numberOfOpenPolicyCancellations: accountDetails.numberOfOpenPolicyCancellations,
            numberOfOpenPolicyChanges: accountDetails.numberOfOpenPolicyChanges
        };

        setupAccountContactDetails(accountDetails.accountHolder);

        function setupAccountContactDetails(accountHolder) {
            const phoneUtils = PhoneNumberUtil.getInstance();
            $scope.accountInfo.subtype = accountHolder.subtype;
            if (accountHolder.subtype === 'Person') {
                $scope.accountInfo.prefix = accountHolder.prefix;
                $scope.accountInfo.firstName = accountHolder.firstName;
                $scope.accountInfo.lastName = accountHolder.lastName;
                 $scope.accountInfo.contactName = nameUtil.getDisplayName(accountHolder, ' ');
                $scope.accountInfo.isPerson = true;
            } else {
                $scope.accountInfo.contactName = accountHolder.displayName;
                $scope.accountInfo.isCommercial = true;
                $scope.accountInfo.organizationTypeName_PSA = (accountHolder.organizationType_PSA ? InsuranceSuiteMetadata.get('pc').types.getTypelist('AccountOrgType').getCode(accountHolder.organizationType_PSA).name : null); // PSA
                $scope.accountInfo.organizationType_PSA = accountHolder.organizationType_PSA; // PSA
                $scope.accountInfo.fiscalCode_PSA = accountHolder.fiscalCode_PSA;
                $scope.accountInfo.companyRegNum_PSA = accountHolder.companyRegNum_PSA;
                $scope.accountInfo.firstName = accountHolder.firstName;
                $scope.accountInfo.lastName = accountHolder.lastName;
                $scope.accountInfo.prefix = accountHolder.prefix;
            }

            $scope.accountInfo.primaryAddress = accountHolder.primaryAddress;
            $scope.displayAddressArray = accountHolder.primaryAddress.displayName.split(', ');
            if (accountHolder.primaryAddress.province != undefined) {
                $scope.displayAddressArray.splice(2, 0, accountHolder.primaryAddress.province);
            }
            // PSA $scope.accountInfo.primaryAddress.stateName = InsuranceSuiteMetadata.get('pc').types.getTypelist('State').getCode(accountHolder.primaryAddress.state).name;
            $scope.accountInfo.primaryPhoneType = accountHolder.primaryPhoneType;
            $scope.accountInfo.homeNumber = accountHolder.homeNumber;
            $scope.accountInfo.workNumber = accountHolder.workNumber;
            $scope.accountInfo.cellNumber = accountHolder.cellNumber;
            $scope.accountInfo.emailAddress1 = accountHolder.emailAddress1;
            $scope.accountInfo.faxNumber = accountHolder.faxNumber; // PSA

            $scope.accountInfo.primaryPhoneNumber = '';
            if (accountHolder.primaryPhoneType === 'work' && angular.isDefined(accountHolder.workNumber) && accountHolder.workNumber.length > 0) {
                $scope.accountInfo.primaryPhoneNumber = phoneUtils.format(phoneUtils.parse(accountHolder.workNumber, defaultLocaleDetails.countryCode));
            } else if (accountHolder.primaryPhoneType === 'home' && angular.isDefined(accountHolder.homeNumber) && accountHolder.homeNumber.length > 0) {
                $scope.accountInfo.primaryPhoneNumber = phoneUtils.format(phoneUtils.parse(accountHolder.homeNumber, defaultLocaleDetails.countryCode));
            } else if (accountHolder.primaryPhoneType === 'mobile' && angular.isDefined(accountHolder.cellNumber) && accountHolder.cellNumber.length > 0) {
                $scope.accountInfo.primaryPhoneNumber = phoneUtils.format(phoneUtils.parse(accountHolder.cellNumber, defaultLocaleDetails.countryCode));
            }
        }
    };

    function getPolicyTransactionStatus() {
        const status = [{
            value: 'all',
            name: '',
            displayKey: 'gateway.views.account-detail.All'
        }, {
            value: 'Draft',
            name: '',
            displayKey: 'typekey.PolicyPeriodStatus.Draft'
        }

        ];

        $scope.policyTransactionTableConfig.policyTransactionStatus = translatePolicyTransactionStatuses(status);
        $scope.policyTransactionTableConfig.transactionStatus = status[0]; // All
    }

    function translatePolicyTransactionStatuses(statuses) {
        statuses.forEach((status) => {
            TranslateService.translate({
                object: status,
                propName: 'name',
                displayKey: status.displayKey,
                scope: $scope
            });
        });

        return statuses;
    }

    function getAccountBillingData(accountNumber) {
        if (!$scope.billingFeatureTurnedOn) { // PSA
            return;
        }
        const accountBillingData = AccountService.getAccountBillingData(accountNumber);
        $scope.accountBillingDataPromise = accountBillingData;

        accountBillingData.then(
            (billingData) => {
                $scope.accountBillingData = billingData;
                checkDelinquencyStatus(billingData);
            },
            () => {
                ModalService.showError('gateway.views.modal.Error', 'gateway.views.account-detail.Error loading billing data');
            }
        );
    }

    function checkDelinquencyStatus(accountBillingData) {
        if (angular.isDefined(accountBillingData) && angular.isDefined(accountBillingData.ownedPolicies) && angular.isDefined(accountBillingData.unownedPolicies)) {
            $scope.accountBillingData.isDelinquent = ((accountBillingData.ownedPolicies.length > 0 && _.filter(accountBillingData.ownedPolicies, {
                    isDelinquent: true
                }).length > 0) ||
                (accountBillingData.unownedPolicies.length > 0 && _.filter(accountBillingData.unownedPolicies, {
                    isDelinquent: true
                }).length > 0));

            if ($scope.accountBillingData.isDelinquent) {
                $scope.billingAndPaymentIcon = 'icon-error fa-exclamation-triangle';
            }
        }
    }

    ctrl.newQuote = () => {
        if (!angular.isDefined($scope.accountInfo.accountNumber)) {
            return;
        }
        const state = $state.get('newQuote');
        $state.transitionTo(state, {'accountNumber': $scope.accountInfo.accountNumber});
    };

    ctrl.showAddActivity = () => {
        $scope.activityTableConfig.showAddActivity = true;
    };

    ctrl.contactTileIsActive = () => {
        return $state.includes('accounts.detail.contacts') || $state.includes('accounts.detail.accountContactDetail') || $state.includes('accounts.detail.relatedContactDetail');
    };

    // Check that the user has the permission to create activities
    ctrl.shouldShowAddActivityButton = () => {
        const permissionDTO = {
            permission: 'actcreate'
        };
        const actPromise = UserService.hasUserSystemPermission(permissionDTO);
        actPromise.then((showActivity) => {
            $scope.showAddActivityButton = showActivity;
        });
    };

    function addRecentlyViewedAccounts() {
        AccountService.addRecentlyViewedAccounts($scope.accountNumber);
    }

    $scope.$on('activitiesUpdated', (event, data) => {
        if (angular.isDefined($scope.accountSummary)) {
            const openActivities = _.filter(data, {
                status: 'open'
            });
            if (openActivities.length !== $scope.accountSummary.numberOfOpenActivities) {
                $scope.accountSummary.numberOfOpenActivities = openActivities.length; // Set the number of open activities
            }
        }
    });

    $scope.$on('notesUpdated', (event, data) => {
        if (angular.isDefined($scope.accountSummary)) {
            $scope.accountSummary.numberOfNotes = data.length;
        }
    });

    $scope.$on('documentsUpdated', (event, data) => {
        if (angular.isDefined($scope.accountSummary)) {
            $scope.accountSummary.numberOfDocuments = data.length;
        }
    });

    return ctrl.create($scope);
};

fn.$inject = ['$translate', '$scope', '$state', '$q', '$stateParams', 'AccountService', 'ActivityService', 'UserService', 'UserInfoService', 'TranslateService', 'ModalService', 'InsuranceSuiteMetadata', 'defaultLocaleDetails'];

export default fn;
